import * as React from "react";
import { useForm, FormProvider } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Loader } from "lucide-react";
const RequestForm = React.lazy(() =>
  import("./RequestForm").then((module) => ({ default: module.RequestForm }))
) as React.FC<{ isGeneratingTitle?: boolean; type: "discuss" | "research" }>;
import { Button } from "../ui/button";
import { useCreateRequest } from "@/hooks/useCreateRequest";
import { RequestGalleryContext } from "./RequestGalleryContext";
import {
  createRequestSchema,
  type CreateRequestFormValues,
} from "@/schemas/request-gallery.schemas";

export type { CreateRequestFormValues };

interface CreateRequestFormProps {
  initialData?: { name: string; content: string };
  type?: "discuss" | "research";
  onCancel?: () => void;
  onSuccess?: () => void;
  isGeneratingTitle?: boolean;
}

export const CreateRequestForm: React.FC<CreateRequestFormProps> = ({
  initialData: propInitialData,
  type: propType,
  onCancel,
  onSuccess,
  isGeneratingTitle,
}) => {
  const context = React.useContext(RequestGalleryContext); // Access context directly or via hook but handle undefined

  // Fallback to context values if props are not provided
  const activeCategoryId = context?.activeCategoryId;
  const handleBack = onCancel ?? context?.handleBack ?? (() => {});
  const initialData = propInitialData ?? context?.initialData;
  const type = propType ?? context?.type ?? "discuss";

  const methods = useForm<CreateRequestFormValues>({
    resolver: zodResolver(createRequestSchema),
    defaultValues: {
      name: initialData?.name ?? "",
      content: initialData?.content ?? "",
      category_id: activeCategoryId ?? "",
      groups: [],
      chain_with: [],
    },
  });

  React.useEffect(() => {
    if (initialData?.name != null) {
      methods.setValue("name", initialData.name);
    }
  }, [initialData?.name, methods.setValue]);

  const createRequestMutation = useCreateRequest();

  const onSubmit = async (data: CreateRequestFormValues) => {
    try {
      await createRequestMutation.mutateAsync({
        type: type,
        ...data,
        category_id: data.category_id ?? null,
        chain_with: data.chain_with.map((item) => item.requestId),
        groups: data.groups,
      });
      if (onSuccess) {
        onSuccess();
      } else {
        handleBack();
      }
    } catch (error) {
      console.error("Error creating request:", error);
    }
  };

  return (
    <FormProvider {...methods}>
      <form onSubmit={methods.handleSubmit(onSubmit)} className="flex flex-col h-full">
        <div className="flex-1 overflow-y-auto p-3">
          <React.Suspense fallback={<Loader className="animate-spin" />}>
            <RequestForm isGeneratingTitle={isGeneratingTitle} type={type} />
          </React.Suspense>
        </div>
        <div className="px-4 py-3 border-t flex justify-end gap-2">
          <Button type="button" variant="outline" size="sm" onClick={handleBack}>
            Cancel
          </Button>
          <Button type="submit" size="sm" disabled={createRequestMutation.isPending}>
            {createRequestMutation.isPending ? "Creating..." : "Create"}
          </Button>
        </div>
      </form>
    </FormProvider>
  );
};
